;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:propstrata-app":true});}catch(e){}}();import { CLIENT_INVALIDATION_EVENT_TYPE } from "@/lib/client-invalidation/catalog";
import type { ClientInvalidationEvent } from "@/lib/client-invalidation/event-schema";
import { agentQueryKeys } from "@/queries/query-keys/agents";
import { commentQueryKeys } from "@/queries/query-keys/comments";
import { etlAutomationQueryKeys } from "@/queries/query-keys/etl-automation";
import { notificationQueryKeys } from "@/queries/query-keys/notifications";
import { usageTrackingQueryKeys } from "@/queries/query-keys/usage-tracking";
import { userQueryKeys } from "@/queries/query-keys/user";
import { visualizationQueryKeys } from "@/queries/query-keys/visualization";
import { workflowQueryKeys } from "@/queries/query-keys/workflows";
import { workspaceQueryKeys } from "@/queries/query-keys/workspace";

function assertNever(value: never): never {
	throw new Error(
		`Unhandled client invalidation event: ${JSON.stringify(value)}`,
	);
}

function workflowListKeys(event: {
	viewVisualizationGroupId?: string;
	workspaceId: string;
}) {
	if (event.viewVisualizationGroupId) {
		return [
			workflowQueryKeys.list({
				viewVisualizationGroupId: event.viewVisualizationGroupId,
				workspaceId: event.workspaceId,
			}),
		];
	}

	return [workflowQueryKeys.workspacePrefix(event.workspaceId)];
}

export function queryKeysForEvent(
	event: ClientInvalidationEvent,
): readonly (readonly unknown[])[] {
	switch (event.type) {
		case CLIENT_INVALIDATION_EVENT_TYPE.executionUpdated:
			return [
				etlAutomationQueryKeys.executionsPrefix({
					automationId: event.automationId,
					workspaceId: event.workspaceId,
				}),
				etlAutomationQueryKeys.execution({
					automationId: event.automationId,
					executionId: event.executionId,
					workspaceId: event.workspaceId,
				}),
			];
		case CLIENT_INVALIDATION_EVENT_TYPE.visualizationStale:
			return [
				visualizationQueryKeys.all(event.workspaceId),
				visualizationQueryKeys.leasingCardSummariesPrefix(event.workspaceId),
			];
		case CLIENT_INVALIDATION_EVENT_TYPE.agentExecutionUpdated:
			return [
				agentQueryKeys.configuredAgentRuns({
					agentId: event.agentId,
					workspaceId: event.workspaceId,
				}),
			];
		case CLIENT_INVALIDATION_EVENT_TYPE.automationStateChanged:
			return [
				etlAutomationQueryKeys.executionsWorkspacePrefix(event.workspaceId),
				etlAutomationQueryKeys.executionWorkspacePrefix(event.workspaceId),
				etlAutomationQueryKeys.supportLocks(event.workspaceId),
				etlAutomationQueryKeys.supportSyncStates(event.workspaceId),
				etlAutomationQueryKeys.supportRunStates(event.workspaceId),
				etlAutomationQueryKeys.supportStuckExecutions(event.workspaceId),
			];
		case CLIENT_INVALIDATION_EVENT_TYPE.commentChanged:
			return [
				commentQueryKeys.list({
					viewVisualizationId: event.viewVisualizationId,
					workspaceId: event.workspaceId,
				}),
				commentQueryKeys.mentions({
					viewVisualizationId: event.viewVisualizationId,
					workspaceId: event.workspaceId,
				}),
			];
		case CLIENT_INVALIDATION_EVENT_TYPE.notificationChanged:
			if (event.workspaceId) {
				return [
					notificationQueryKeys.list(event.workspaceId),
					notificationQueryKeys.unreadSummary(event.workspaceId),
				];
			}

			return [notificationQueryKeys.all];
		case CLIENT_INVALIDATION_EVENT_TYPE.alertChanged:
			return workflowListKeys(event);
		case CLIENT_INVALIDATION_EVENT_TYPE.workflowChanged:
			return workflowListKeys(event);
		case CLIENT_INVALIDATION_EVENT_TYPE.workspaceMembershipChanged:
			return [
				workspaceQueryKeys.users(event.workspaceId),
				workspaceQueryKeys.userCount(event.workspaceId),
				workspaceQueryKeys.invitations(event.workspaceId),
			];
		case CLIENT_INVALIDATION_EVENT_TYPE.userSettingsChanged:
			return [userQueryKeys.detail(event.userId), userQueryKeys.settings()];
		case CLIENT_INVALIDATION_EVENT_TYPE.usageTrackingChanged:
			return [usageTrackingQueryKeys.all];
		default:
			return assertNever(event);
	}
}

export function queryKeysForReconnect(session: {
	organizationId?: string;
	userId?: string;
	workspaceId?: string;
}): readonly (readonly unknown[])[] {
	const queryKeys: (readonly unknown[])[] = [];

	if (session.workspaceId) {
		queryKeys.push(
			etlAutomationQueryKeys.executionsWorkspacePrefix(session.workspaceId),
			etlAutomationQueryKeys.executionWorkspacePrefix(session.workspaceId),
			etlAutomationQueryKeys.supportLocks(session.workspaceId),
			etlAutomationQueryKeys.supportSyncStates(session.workspaceId),
			etlAutomationQueryKeys.supportRunStates(session.workspaceId),
			etlAutomationQueryKeys.supportStuckExecutions(session.workspaceId),
			agentQueryKeys.configuredAgentRunsPrefix(session.workspaceId),
			commentQueryKeys.workspacePrefix(session.workspaceId),
			commentQueryKeys.mentionsPrefix(session.workspaceId),
			notificationQueryKeys.list(session.workspaceId),
			notificationQueryKeys.unreadSummary(session.workspaceId),
			workflowQueryKeys.workspacePrefix(session.workspaceId),
			workspaceQueryKeys.users(session.workspaceId),
			workspaceQueryKeys.userCount(session.workspaceId),
			workspaceQueryKeys.invitations(session.workspaceId),
			visualizationQueryKeys.all(session.workspaceId),
			visualizationQueryKeys.leasingCardSummariesPrefix(session.workspaceId),
		);
	}

	if (session.userId) {
		queryKeys.push(
			userQueryKeys.detail(session.userId),
			userQueryKeys.settings(),
		);
	}

	if (session.organizationId) {
		queryKeys.push(usageTrackingQueryKeys.all);
	}

	return queryKeys;
}
